import type * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { MountedDraftKind } from "@steelbill/types";
import { DraftNameBadge, draftName } from "@/features/drafts/DraftNameBadge";
import {
  DRAFT_LIST_HELP_KEY,
  DRAFT_LIST_TITLE_KEY,
  DraftStatusBadge,
  draftResumeHref,
} from "@/features/drafts/draft-kind";
import {
  useDeleteDraft,
  useDrafts,
  type DraftSummary,
} from "@/features/drafts/use-document-draft";
import { SortableHead } from "@/components/SortableHead";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useFormatDay, istDayOfInstant } from "@/lib/dates";
import { cn } from "@/lib/utils";

/**
 * Unfinished work, above its own register (§7.2 amendment).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * ONE COMPONENT, EVERY REGISTER. This lived inside `InvoiceListScreen` and was
 * invoice-shaped, with a comment explaining that quotation drafts "belong to the
 * quotation register" — which was true, and the quotation register did not have
 * one. So a quotation draft existed, autosaved correctly, and appeared nowhere
 * the owner would look for it: he starts an offer on the shop phone, gets called
 * away, and next morning taps Quotations to find only numbered documents.
 *
 * KIND-SCOPED, NOT MERGED. Each register shows only its own kind, because
 * "Continue" must open an editor that can actually read the payload — an invoice
 * draft resumed into the quotation editor would restore lines into a form with
 * no S.O. fields and silently drop them on the next autosave. The home screen's
 * list is the one place every kind appears together, and it labels each row.
 *
 * IT IS THE REGISTER'S TABLE, NOT A TABLE THAT RESEMBLES IT. Same card, same
 * breakpoint, same two layouts, the same `SortableHead`, the same `TONE` palette
 * on the status badge, and the number set in the register's plain
 * `font-medium tabular-nums` rather than in a pill of its own. Unfinished work
 * and finished documents are one list to the owner, divided only by whether a
 * number has been spent yet — anything that makes the top table look like a
 * different KIND of object is a difference he has to think about for no reason.
 *
 * WHAT IT DELIBERATELY HAS NO COLUMN FOR IS THE TOTAL, though the register's
 * last column is one. A draft is a saved FORM: no server has computed anything
 * about it, and §6 gives the server sole authority over every figure. A total
 * here could only be computed in the client, which CLAUDE.md forbids outright —
 * and a wrong money column on the screen where the owner decides what to finish
 * is worse than no money column.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/** The columns a draft can be ordered by. `actions` is not one of them. */
type DraftSort = "number" | "started" | "lastSaved" | "customer" | "status";

export function DraftsStrip({
  kind,
}: {
  kind: MountedDraftKind;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const formatDay = useFormatDay();
  const drafts = useDrafts(kind);
  const remove = useDeleteDraft(kind);

  /*
   * SORTED IN THE CLIENT, AND THAT IS THE CORRECT ANSWER HERE RATHER THAN A
   * SHORTCUT.
   *
   * The register sorts on the SERVER because it is paginated: ordering only the
   * twenty-five rows already fetched would put the largest invoice of the year
   * on page 3 and leave page 1 claiming to be sorted. `GET /<kind>/drafts`
   * takes no sort parameter and needs none — it returns the WHOLE list, because
   * unfinished work is a handful of rows by definition, so a client-side sort is
   * complete and therefore honest.
   *
   * If drafts ever gain pagination this must move to the server on the same day.
   */
  const [sort, setSort] = useState<DraftSort | undefined>(undefined);
  const [dir, setDir] = useState<"asc" | "desc" | undefined>(undefined);

  const rows = sortDrafts(drafts.data ?? [], sort, dir);
  // Nothing unfinished is the ordinary case; an empty card would be a permanent
  // reminder of a state that is not a problem.
  if (rows.length === 0) return null;

  const onSort = (
    next: DraftSort | undefined,
    nextDir: "asc" | "desc" | undefined,
  ) => {
    setSort(next);
    setDir(nextDir);
  };

  /*
   * THE IST DAY OF THE INSTANT, not `createdAt.slice(0, 10)`. That slice takes
   * the UTC day and prints it as an IST one, so anything touched between 00:00
   * and 05:30 IST reported yesterday — the owner works late, and a draft he
   * started after midnight read as the previous day's.
   */
  const day = (instant: string) => formatDay(istDayOfInstant(instant));

  const customerOf = (draft: DraftSummary) =>
    draft.customerName === "" ? t("lbl.customerUnknown") : draft.customerName;

  const actionsFor = (draft: DraftSummary, align?: string) => (
    <DraftActions
      kind={kind}
      draft={draft}
      busy={remove.isPending}
      onDiscard={() => {
        remove.mutate(draft.id);
      }}
      className={align}
    />
  );

  return (
    <>
      {/*
       * `gap-0` MATTERS AS MUCH AS `py-0`. `Card` is `flex flex-col
       * gap-(--card-spacing)`, so zeroing the padding still leaves 16px of flex
       * gap between every child — a dead stripe between the caption band and
       * the table, with the band's bottom rule stranded above it.
       *
       * The register never showed this because its card holds exactly ONE
       * child. This one holds three, and the gap appears twice.
       */}
      <Card className="gap-0 overflow-hidden py-0">
        {/*
         * A caption band, not a `CardHeader`. `CardHeader`'s scale announces a new
         * region of the page; this is a heading over rows, so it takes the table
         * header's weight and its bottom rule instead.
         *
         * TITLE AND HELP STAY STACKED, and the band keeps `py-3`. Collapsing them
         * onto one line from `md:` up was tried on 2026-08-16 and reverted: it
         * bought about 30px above a table that is usually one row tall, and cost
         * the heading its own line — the sentence explaining that these have no
         * number and can be thrown away ran on from the title as though it were
         * part of it.
         *
         * The table's own padding is a separate question and is NOT the place to
         * reclaim height: `p-2` cells and `h-10` heads are the register's exactly,
         * which is the entire point of the two tables matching.
         */}
        <div className="bg-muted/30 border-b px-4 py-3 md:px-2">
          <p className="text-sm font-medium">{t(DRAFT_LIST_TITLE_KEY[kind])}</p>
          <p className="text-muted-foreground text-xs">
            {t(DRAFT_LIST_HELP_KEY[kind])}
          </p>
        </div>

        {/*
         * Phone: one stacked row per draft, matching the register's mobile cards.
         * A six-column table on a 360px screen either scrolls sideways or crushes
         * the two controls into something un-tappable, and the phone is where this
         * screen is actually read — standing in the workshop.
         *
         * THE BADGE SURVIVES HERE, and only here. With no column headings to say
         * what a bare `DRAFT/INV/6` is, the pill is what marks it as a name rather
         * than a document number.
         */}
        <ul className="flex flex-col md:hidden">
          {rows.map((draft) => (
            <li
              key={draft.id}
              className="flex min-h-14 flex-wrap items-center justify-between gap-3 border-b px-4 py-3 last:border-b-0"
            >
              <div className="flex min-w-0 flex-col gap-1">
                <span className="flex flex-wrap items-center gap-2 font-medium">
                  <DraftNameBadge
                    kind={kind}
                    id={draft.id}
                    status={draft.status}
                    className="shrink-0"
                  />
                  <span className="truncate">{customerOf(draft)}</span>
                </span>
                <span className="text-muted-foreground text-xs">
                  {t("draft.started", { when: day(draft.createdAt) })}
                </span>
              </div>
              {actionsFor(draft)}
            </li>
          ))}
        </ul>

        {/*
         * Desktop: the register's own table, sortable heads and all.
         *
         * HIDDEN ON A WRAPPER, NOT ON THE `<table>` ITSELF. `Table` renders its
         * own `overflow-x-auto` container around the element, so `hidden md:table`
         * on the table left that container in the layout on a phone — an empty,
         * zero-height flex child that still took a gap and could still show a
         * scrollbar. Hiding the wrapper removes the whole thing from the flow.
         */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead
                  column="number"
                  label={t("lbl.number")}
                  sort={sort}
                  dir={dir}
                  onSort={onSort}
                />
                <SortableHead
                  column="started"
                  label={t("lbl.started")}
                  sort={sort}
                  dir={dir}
                  onSort={onSort}
                />
                <SortableHead
                  column="lastSaved"
                  label={t("lbl.lastSaved")}
                  sort={sort}
                  dir={dir}
                  onSort={onSort}
                />
                <SortableHead
                  column="customer"
                  label={t("lbl.customer")}
                  sort={sort}
                  dir={dir}
                  onSort={onSort}
                />
                <SortableHead
                  column="status"
                  label={t("lbl.status")}
                  sort={sort}
                  dir={dir}
                  onSort={onSort}
                />
                {/* Not sortable, and no caret: ordering by a pair of buttons is not
                 * a question anyone has. The register's last column is `Total`,
                 * which IS sortable — the asymmetry is the absent money, not a
                 * missing feature. */}
                <TableHead className="text-right">{t("lbl.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((draft) => (
                <TableRow key={draft.id}>
                  <TableCell className="font-medium tabular-nums">
                    {/*
                     * PLAIN TEXT IN THE REGISTER'S OWN NUMBER STYLE, not the amber
                     * pill this column used to carry. With a Status column beside
                     * it saying "Not finished" in words, the pill was stating the
                     * same fact a second time in a shape no other number on the
                     * screen wears — and it made the top table read as a different
                     * kind of object from the one below it.
                     *
                     * The NAME is unchanged: `DRAFT/INV/6` still leads with its
                     * state, because that is what makes it sayable out loud and
                     * impossible to mistake for a document number (§6 — a draft has
                     * no number until it is saved).
                     *
                     * LINKED LIKE THE REGISTER'S NUMBER, to the only place a draft
                     * can be opened. Deliberately WITHOUT the register's
                     * `after:absolute after:inset-0` full-row overlay: that makes
                     * the whole row one click target, which here would sit on top of
                     * Continue and Throw away and swallow both.
                     */}
                    <Link
                      to={draftResumeHref(kind, draft.id)}
                      className="hover:underline"
                    >
                      {draftName(kind, draft.id, draft.status)}
                    </Link>
                  </TableCell>
                  {/*
                   * STARTED AND LAST SAVED ARE DIFFERENT COLUMNS BECAUSE THEY ARE
                   * DIFFERENT FACTS, and until this table there was only one line
                   * reading "started …" — which was rendering `updatedAt`. A draft
                   * begun on Monday and touched on Friday reported "started
                   * Friday". `DraftSummary` has carried both all along.
                   *
                   * LAST SAVED answers the question the owner actually has in front
                   * of this list: is the thing I typed on the shop phone an hour ago
                   * safely on the server?
                   */}
                  <TableCell className="tabular-nums whitespace-nowrap">
                    {day(draft.createdAt)}
                  </TableCell>
                  <TableCell className="tabular-nums whitespace-nowrap">
                    {day(draft.updatedAt)}
                  </TableCell>
                  <TableCell className="max-w-[24ch] truncate">
                    {customerOf(draft)}
                  </TableCell>
                  <TableCell>
                    <DraftStatusBadge status={draft.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    {actionsFor(draft, "justify-end")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
      {/*
       * THE RULE BETWEEN UNFINISHED WORK AND THE REGISTER (owner's ask,
       * 2026-08-16). Two flush-edged cards of near-identical rows, a short gap
       * apart, read as one long table that changes its mind halfway down — and
       * the second one starts with a search box, which is a strange thing to
       * find in the middle of a list.
       *
       * IT LIVES IN HERE RATHER THAN IN THE THREE SCREENS, and that placement
       * is the whole reason it is correct. This component returns `null` when
       * nothing is unfinished, which is the ordinary case; a separator written
       * into each register would then draw a rule above the filter bar with
       * nothing at all above it. Inside, it cannot outlive the block it
       * separates.
       *
       * One copy also means the invoice, quotation and challan registers
       * cannot drift apart on it — the same reason the table itself lives here.
       */}
      <Separator />
    </>
  );
}

/**
 * Order the rows, or hand back the server's order untouched.
 *
 * `undefined` IS A REAL STATE, not "no sort yet". The list arrives
 * newest-touched-first, which is the right default for a to-do list, and
 * `SortableHead`'s third press returns to exactly that — so clearing a sort must
 * restore the server's sequence rather than fall back to some other column.
 *
 * NON-MUTATING. `Array.prototype.sort` sorts in place, and the array handed in
 * belongs to TanStack Query's cache; sorting it directly would reorder the cached
 * value without React knowing, so the next render of any other consumer would
 * show a different order for no reason.
 */
export function sortDrafts(
  rows: readonly DraftSummary[],
  sort: DraftSort | undefined,
  dir: "asc" | "desc" | undefined,
): DraftSummary[] {
  if (sort === undefined) return [...rows];

  const sign = dir === "asc" ? 1 : -1;
  const by = (a: DraftSummary, b: DraftSummary): number => {
    switch (sort) {
      case "number":
        /*
         * BY ID, as the register sorts its own number column — and for the same
         * reason. `DRAFT/INV/6` is TEXT, and sorting text puts /9 after /67.
         */
        return a.id - b.id;
      case "started":
        // ISO-8601 in UTC sorts correctly as text: every timestamp off this API
        // is that shape, so there is nothing to parse and no zone to get wrong.
        return a.createdAt.localeCompare(b.createdAt);
      case "lastSaved":
        return a.updatedAt.localeCompare(b.updatedAt);
      case "customer":
        // `localeCompare` so "Ãshok" files where a reader expects it, not after Z.
        return a.customerName.localeCompare(b.customerName);
      case "status":
        return a.status.localeCompare(b.status);
    }
  };

  // `id` breaks every tie, so two drafts saved in the same minute cannot swap
  // places between renders — a list that reshuffles on its own reads as a bug.
  return [...rows].sort((a, b) => sign * by(a, b) || b.id - a.id);
}

/**
 * The two controls, shared by both layouts so they cannot drift apart.
 *
 * Default height across the row — mixed h-7/h-8 controls in one line read as an
 * accident rather than a hierarchy.
 */
function DraftActions({
  kind,
  draft,
  busy,
  onDiscard,
  className,
}: {
  kind: MountedDraftKind;
  draft: DraftSummary;
  busy: boolean;
  onDiscard: () => void;
  className?: string;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className={cn("flex shrink-0 gap-2", className)}>
      <Button
        nativeButton={false}
        render={<Link to={draftResumeHref(kind, draft.id)} />}
      >
        {t("draft.resume")}
      </Button>
      <Button variant="outline" disabled={busy} onClick={onDiscard}>
        {t("draft.discard")}
      </Button>
    </div>
  );
}
